import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CustomizationRules.css';
import { FaTimes, FaPlus, FaTrash, FaInfoCircle } from 'react-icons/fa';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const CustomizationRules = ({ businessId, isOpen, onClose }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [recipeIngredients, setRecipeIngredients] = useState([]);
  const [selectedIngredient, setSelectedIngredient] = useState(null);
  const [rules, setRules] = useState([]);
  const [commonModifiers, setCommonModifiers] = useState({});
  const [customModifier, setCustomModifier] = useState('');
  const [customScalar, setCustomScalar] = useState('1.0');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch menu items on mount
  useEffect(() => {
    if (isOpen && businessId) {
      fetchMenuItems();
      fetchCommonModifiers();
    }
  }, [isOpen, businessId]);

  // Fetch recipe ingredients when menu item is selected
  useEffect(() => {
    if (selectedMenuItem) {
      fetchRecipeIngredients(selectedMenuItem.menu_item_id);
    }
  }, [selectedMenuItem]);

  // Fetch rules when ingredient is selected
  useEffect(() => {
    if (selectedMenuItem && selectedIngredient) {
      fetchRules(selectedMenuItem.menu_item_id);
    }
  }, [selectedMenuItem, selectedIngredient]);

  const fetchMenuItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/customization-rules/businesses/${businessId}/menu-items`);
      setMenuItems(response.data || []);
    } catch (err) {
      setError('Failed to fetch menu items');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommonModifiers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/customization-rules/common-modifiers`);
      const modifiers = response.data.data || [];
      // Convert array to object for easy lookup
      const modifiersObj = {};
      modifiers.forEach(mod => {
        modifiersObj[mod.modifierTag] = mod;
      });
      setCommonModifiers(modifiersObj);
    } catch (err) {
      console.error('Failed to fetch common modifiers:', err);
    }
  };

  const fetchRecipeIngredients = async (menuItemId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/customization-rules/menu-items/${menuItemId}/recipe-ingredients`);
      const ingredients = response.data || [];
      setRecipeIngredients(ingredients);
      if (ingredients.length > 0) {
        setSelectedIngredient(ingredients[0]);
      }
    } catch (err) {
      console.error('Failed to fetch recipe ingredients:', err);
      setRecipeIngredients([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRules = async (menuItemId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/customization-rules/menu-item/${menuItemId}`
      );
      const rulesData = response.data.data || response.data || [];
      // The API returns grouped rules by ingredient, flatten them
      const flatRules = [];
      if (Array.isArray(rulesData)) {
        rulesData.forEach(group => {
          if (group.rules && Array.isArray(group.rules)) {
            group.rules.forEach(rule => {
              flatRules.push({
                ...rule,
                ingredient_name: group.ingredient_name,
                unit: group.unit
              });
            });
          }
        });
      } else if (typeof rulesData === 'object') {
        Object.values(rulesData).forEach(group => {
          if (group.rules && Array.isArray(group.rules)) {
            group.rules.forEach(rule => {
              flatRules.push({
                ...rule,
                ingredient_name: group.ingredient_name,
                unit: group.unit
              });
            });
          }
        });
      }
      setRules(flatRules);
    } catch (err) {
      console.error('Failed to fetch rules:', err);
      setRules([]);
    }
  };

  const createRule = async (modifierTag, scalar) => {
    if (!selectedMenuItem || !selectedIngredient) return;

    try {
      await axios.post(
        `${API_BASE_URL}/customization-rules`,
        {
          menuItemId: selectedMenuItem.menu_item_id,
          inventoryItemId: selectedIngredient.ingredient_item_id,
          modifierTag: modifierTag,
          deductionScalar: parseFloat(scalar)
        },
        { headers: { 'x-business-id': businessId } }
      );
      
      // Refresh rules
      await fetchRules(selectedMenuItem.menu_item_id);
      setCustomModifier('');
      setCustomScalar('1.0');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create rule');
    }
  };

  const deleteRule = async (ruleId) => {
    try {
      await axios.delete(`${API_BASE_URL}/customization-rules/${ruleId}`);
      // Refresh rules
      await fetchRules(selectedMenuItem.menu_item_id);
    } catch (err) {
      setError('Failed to delete rule');
    }
  };

  const getIngredientRules = (ingredientId) => {
    return rules.filter(rule => 
      rule.inventory_item_id === ingredientId || 
      rule.ingredient_item_id === ingredientId
    );
  };

  if (!isOpen) return null;

  return (
    <div className="cr-modal-overlay">
      <div className="cr-modal-container">
        <div className="cr-modal-header">
          <h2 className="cr-modal-title">Customization Rules</h2>
          <button className="cr-close-button" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="cr-modal-content">
          {/* Info Banner */}
          <div className="cr-info-banner">
            <FaInfoCircle className="cr-info-icon" />
            <div className="cr-info-text">
              <strong>Dynamic Ingredient Scalars (DIS)</strong> - Configure how modifiers affect ingredient quantities. 
              Example: "NO SUGAR" = 0.0x (no sugar deducted), "EXTRA CHEESE" = 1.5x (50% more cheese).
            </div>
          </div>

          {/* Menu Item Selection */}
          <div className="cr-form-section">
            <label className="cr-form-label">Select Menu Item</label>
            <select
              className="cr-form-select"
              value={selectedMenuItem?.menu_item_id || ''}
              onChange={(e) => {
                const item = menuItems.find(m => m.menu_item_id === parseInt(e.target.value));
                setSelectedMenuItem(item);
                setSelectedIngredient(null);
                setRules([]);
              }}
            >
              <option value="">Choose a menu item...</option>
              {menuItems.map(item => (
                <option key={item.menu_item_id} value={item.menu_item_id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>

          {/* Ingredient Selection */}
          {selectedMenuItem && recipeIngredients.length > 0 && (
            <div className="cr-form-section">
              <label className="cr-form-label">Select Ingredient</label>
              <select
                className="cr-form-select"
                value={selectedIngredient?.ingredient_item_id || ''}
                onChange={(e) => {
                  const ingredient = recipeIngredients.find(i => i.ingredient_item_id === parseInt(e.target.value));
                  setSelectedIngredient(ingredient);
                }}
              >
                {recipeIngredients.map(ingredient => (
                  <option key={ingredient.ingredient_item_id} value={ingredient.ingredient_item_id}>
                    {ingredient.item_name} ({ingredient.quantity_required} {ingredient.unit_symbol})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Common Modifiers - Quick Add */}
          {selectedMenuItem && selectedIngredient && (
            <>
              <div className="cr-section-divider">
                <span className="cr-section-title">Common Modifiers</span>
              </div>

              <div className="cr-modifiers-grid">
                {Object.entries(commonModifiers).map(([key, modifier]) => {
                  const ingredientRules = getIngredientRules(selectedIngredient.ingredient_item_id);
                  const hasRule = ingredientRules.some(r => r.modifier_tag === modifier.modifierTag);
                  
                  return (
                    <button
                      key={key}
                      className={`cr-modifier-button ${hasRule ? 'active' : ''}`}
                      onClick={() => !hasRule && createRule(modifier.modifierTag, modifier.scalar)}
                      disabled={hasRule}
                    >
                      <div className="cr-modifier-tag">{modifier.modifierTag}</div>
                      <div className="cr-modifier-scalar">{modifier.scalar}×</div>
                      <div className="cr-modifier-desc">{modifier.description}</div>
                    </button>
                  );
                })}
              </div>

              {/* Custom Modifier */}
              <div className="cr-section-divider">
                <span className="cr-section-title">Custom Modifier</span>
              </div>

              <div className="cr-custom-modifier">
                <input
                  type="text"
                  className="cr-form-input"
                  placeholder="Modifier tag (e.g., HALF)"
                  value={customModifier}
                  onChange={(e) => setCustomModifier(e.target.value.toUpperCase())}
                />
                <input
                  type="number"
                  className="cr-form-input"
                  placeholder="Scalar"
                  min="0"
                  max="3"
                  step="0.1"
                  value={customScalar}
                  onChange={(e) => setCustomScalar(e.target.value)}
                />
                <button
                  className="cr-add-button"
                  onClick={() => customModifier && createRule(customModifier, customScalar)}
                  disabled={!customModifier}
                >
                  <FaPlus /> Add
                </button>
              </div>

              {/* Active Rules for Selected Ingredient */}
              <div className="cr-section-divider">
                <span className="cr-section-title">Active Rules for {selectedIngredient.item_name}</span>
              </div>

              <div className="cr-rules-list">
                {getIngredientRules(selectedIngredient.ingredient_item_id).length === 0 ? (
                  <div className="cr-empty-state">
                    No customization rules yet. Add modifiers above to control ingredient deductions.
                  </div>
                ) : (
                  getIngredientRules(selectedIngredient.ingredient_item_id).map(rule => (
                    <div key={rule.rule_id} className="cr-rule-item">
                      <div className="cr-rule-info">
                        <div className="cr-rule-tag">{rule.modifier_tag}</div>
                        <div className="cr-rule-details">
                          <span className="cr-rule-scalar">{parseFloat(rule.deduction_scalar)}x</span>
                          <span className="cr-rule-calculation">
                            {selectedIngredient.quantity_required} × {parseFloat(rule.deduction_scalar)} = 
                            <strong> {(selectedIngredient.quantity_required * parseFloat(rule.deduction_scalar)).toFixed(2)} {selectedIngredient.unit_symbol}</strong>
                          </span>
                        </div>
                      </div>
                      <button
                        className="cr-delete-button"
                        onClick={() => deleteRule(rule.rule_id)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {/* Empty State */}
          {!selectedMenuItem && (
            <div className="cr-empty-state-large">
              <FaInfoCircle className="cr-empty-icon" />
              <h3>Get Started</h3>
              <p>Select a menu item to configure customization rules for its ingredients.</p>
            </div>
          )}

          {selectedMenuItem && recipeIngredients.length === 0 && (
            <div className="cr-empty-state-large">
              <FaInfoCircle className="cr-empty-icon" />
              <h3>No Recipe Found</h3>
              <p>This menu item doesn't have a recipe. Add ingredients in the Recipe Builder first.</p>
            </div>
          )}

          {error && (
            <div className="cr-error-message">
              {error}
              <button onClick={() => setError(null)}>×</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomizationRules;
